// NOTICE: KAPTION-INTEGRATION
// team-availability-guard.UI — a team nobody is connected to is not an assignment
// target.
//
// Assigning to an empty team is how a conversation ends up owned by a queue no one is
// reading: it looks assigned, it leaves the unassigned list, and nothing brings it back.
// The count rides on the team payload (online_member_count) so this file does no
// fetching and no arithmetic.
//
// `null` means UNKNOWN, not zero. The field is absent whenever the account has the guard
// off, and a client must never grey out a team because the server did not compute it.

import { availabilityIcon } from 'dashboard/helper/commandbar/agentAvailability';

export const teamOnlineCount = team =>
  Number.isInteger(team?.online_member_count) ? team.online_member_count : null;

// team-availability-guard.UI.1
export const isTeamAssignable = team => {
  const count = teamOnlineCount(team);
  return count === null || count > 0;
};

// team-availability-guard.UI.2 — the suffix the pickers append.
//
// Only the rows you CAN pick say anything. A team with nobody connected is already
// telling you so by carrying no green dot and by not responding to a click; a label as
// well turns a quiet "not this one" into a row that argues with the reader. Empty string
// when the guard is off, so the label is exactly today's label.
export const teamAvailabilityLabel = (team, t) => {
  const count = teamOnlineCount(team);
  if (count === null || count === 0) return '';

  return t('CONVERSATION.TEAM_AVAILABILITY.ONLINE_COUNT', { count });
};

// team-availability-guard.UI.3 — the sidebar dropdown draws its own presence dot from
// `availability_status`, with `hide-offline-status` set: 'online' gets the green dot,
// anything else gets nothing. Reusing that is how the two pickers end up with the same
// mark without this file knowing how either of them renders.
export const teamAvailabilityStatus = team =>
  teamOnlineCount(team) > 0 ? 'online' : undefined;

export const teamTitleWithAvailability = (team, t) => {
  const label = teamAvailabilityLabel(team, t);
  return label ? `${team.name} · ${label}` : team.name;
};

// team-availability-guard.UI.3 — a green dot marks a team you can hand work to. A team
// with nobody connected gets NO dot: absence is the signal, and it reads as "not
// available" without giving the reader a second thing to decode.
//
// The dot is the agents' own online dot, borrowed rather than redrawn, so a team that
// can take work is marked exactly like a person who can. It matters that this is the
// same function and not a copy: these tokens hold RGB CHANNELS ("20 184 166"), so the
// markup needs rgb(var(--teal-10)) — a bare var() is an invalid fill and paints nothing
// at all, which is a blank cell rather than a visible error.
export const teamAvailabilityIcon = team => {
  const count = teamOnlineCount(team);
  if (count === null || count === 0) return null;

  return availabilityIcon('online');
};
